import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  ExpandMore,
  ChevronRight,
  FolderRounded,
  HomeRounded,
  Search,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import { ThemeColor } from "../../component/theme/color";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import {
  GetAllFoldersService,
  GetFolderlistService,
  GetFolderAndFilesByParentlistService,
} from "../../Redux/service/FileManagerService";
import CustomButton from "../../component/button/button";
import {
  buildFolderOptions,
  buildVisibleRows,
  findSearchRoots,
} from "./moveToTree";
import { getFileIcon } from "./fileIcon.util";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: 560, md: 680 },
  maxWidth: "100%",
  height: { xs: "88vh", sm: "80vh" },
  maxHeight: "88vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(16, 24, 40, 0.22)",
  p: { xs: 2.5, sm: 3 },
  outline: "none",
};

export const FileManagerPickerModal = ({ open, handleClose, onSelectFile, allowedExtensions = ["xlsx", "xls"] }) => {
  const dispatch = useDispatch();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // ids of expanded folders, plus "root"
  const [expanded, setExpanded] = useState(() => new Set(["root"]));
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Maps folder ID (or 'root') to array of files
  const [filesByFolder, setFilesByFolder] = useState({});
  const [fetchingFiles, setFetchingFiles] = useState({});

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setExpanded(new Set(["root"]));
    setSelectedFile(null);
    setFilesByFolder({});
    setLoading(true);

    Promise.all([
      GetAllFoldersService().catch(() => ({ data: { data: [] } })),
      GetFolderlistService().catch(() => ({ data: { data: [] } }))
    ])
      .then(([allFoldersRes, rootRes]) => {
        setFolders(allFoldersRes.data?.data || []);
        
        // Extract root files from GetFolderlistService
        const rootData = rootRes.data?.data || [];
        const rootFiles = rootData.filter(item => item.type !== "folder");
        setFilesByFolder(prev => ({ ...prev, root: rootFiles }));
      })
      .catch((e) => {
        dispatch(
          showSnackbar(
            e.response?.data?.message || "Failed to load folders",
            "error"
          )
        );
      })
      .finally(() => setLoading(false));
  }, [open, dispatch]);

  const fetchFilesInFolder = async (folderId) => {
    if (filesByFolder[folderId] || fetchingFiles[folderId]) return;
    
    setFetchingFiles(prev => ({ ...prev, [folderId]: true }));
    try {
      const res = await GetFolderAndFilesByParentlistService(folderId);
      if (res.status === 200) {
        const items = res.data?.data || [];
        const fetchedFiles = Array.isArray(items) ? items.filter(item => item.type !== "folder") : (items.files || []);
        setFilesByFolder(prev => ({ ...prev, [folderId]: fetchedFiles }));
      }
    } catch (e) {
      console.error("Failed to fetch files for folder", folderId, e);
    } finally {
      setFetchingFiles(prev => ({ ...prev, [folderId]: false }));
    }
  };

  const options = useMemo(() => buildFolderOptions(folders, null), [folders]);

  const { matches, roots: searchRoots } = useMemo(
    () => findSearchRoots(options, search),
    [options, search]
  );

  useEffect(() => {
    if (!searchRoots?.length) return;
    setExpanded((prev) => new Set([...prev, ...searchRoots.map((r) => r.id)]));
  }, [searchRoots]);

  const visibleOptions = useMemo(
    () => buildVisibleRows({ options, search, expanded, roots: searchRoots }),
    [options, search, expanded, searchRoots]
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (id !== "root") fetchFilesInFolder(id);
      }
      return next;
    });
  };

  const isSearching = Boolean(search.trim());

  const getExtension = (file) => {
    const extFrom = (value) => {
      if (!value) return null;
      const lastSegment = String(value).split("?")[0].split("/").pop() || "";
      if (!lastSegment.includes(".")) return null;
      return lastSegment.split(".").pop().toLowerCase();
    };
    return extFrom(file.name) || extFrom(file.url) || "";
  };

  const handleFileClick = (file) => {
    const ext = getExtension(file);
    if (!allowedExtensions.includes(ext)) {
      dispatch(showSnackbar(`Only ${allowedExtensions.join(", ")} files are allowed`, "error"));
      return;
    }
    setSelectedFile(file);
  };

  const handleSelect = () => {
    if (selectedFile) {
      onSelectFile(selectedFile);
      handleClose();
    }
  };

  const renderFileRow = (file, depth) => {
    const ext = getExtension(file);
    const isAllowed = allowedExtensions.includes(ext);
    const isSelected = selectedFile?.id === file.id || selectedFile?._id === file._id;
    const fileIcon = getFileIcon(file.name, file.url);

    return (
      <ListItemButton
        key={file.id || file._id}
        disabled={!isAllowed}
        selected={isSelected}
        onClick={() => handleFileClick(file)}
        sx={{
          pl: 2 + depth * 3,
          py: 0.5,
          "&.Mui-selected": { bgcolor: "rgba(102, 108, 255, 0.08)" },
          "&.Mui-selected:hover": { bgcolor: "rgba(102, 108, 255, 0.12)" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 34 }}>
          <img src={fileIcon} alt="icon" width={20} height={20} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontFamily: FontFamily.Poppins,
            fontWeight: fontWeight.W500,
            fontSize: fontSize.h4.medium,
            color: isAllowed ? ThemeColor.BLACK : "#98a2b3",
            sx: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          primary={file.name}
        />
      </ListItemButton>
    );
  };

  return (
    <Modal open={Boolean(open)} onClose={handleClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={fontWeight.W600}
            fontSize={fontSize.h3.large}
            color={ThemeColor.BLACK}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Select A File From CRM
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#667085" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search folders..."
          size="small"
          fullWidth
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontFamily: FontFamily.Poppins,
              fontSize: fontSize.h4.large,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#98a2b3" }} fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #e4e7ec",
            borderRadius: "10px",
            minHeight: 240,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 240,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense disablePadding>
              {!isSearching && (
                <>
                  <ListItemButton
                    onClick={() => toggleExpand("root")}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <IconButton
                        size="small"
                        sx={{ p: 0.5, mr: 0.5 }}
                        edge="start"
                      >
                        {expanded.has("root") ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <HomeRounded sx={{ color: ThemeColor.PURPLE }} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontFamily: FontFamily.Poppins,
                        fontWeight: fontWeight.W600,
                        fontSize: fontSize.h4.large,
                      }}
                      primary="My Folder (Root)"
                    />
                  </ListItemButton>
                  
                  {/* Root files */}
                  {expanded.has("root") &&
                    (filesByFolder["root"] || []).map((file) =>
                      renderFileRow(file, 2)
                    )}
                </>
              )}

              {visibleOptions.map((opt) => {
                const folderId = opt.id;
                const isExpanded = expanded.has(folderId);
                const hasChildren = opt.hasChildren || (filesByFolder[folderId] && filesByFolder[folderId].length > 0);
                // In search mode, matches show their full path if collapsed so you
                // know what you found, or if they head their own result tree.
                const showPath = isSearching && (!isExpanded || searchRoots.includes(opt));
                
                return (
                  <React.Fragment key={folderId}>
                    <ListItemButton
                      onClick={() => toggleExpand(folderId)}
                      sx={{
                        pl: 2 + opt.depth * 3,
                        py: 0.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.5, mr: 0.5 }}
                          edge="start"
                        >
                          {isExpanded ? (
                            <ExpandMore fontSize="small" />
                          ) : (
                            <ChevronRight fontSize="small" />
                          )}
                        </IconButton>
                      </ListItemIcon>
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <FolderRounded sx={{ color: "#FBBF24" }} />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{
                          fontFamily: FontFamily.Poppins,
                          fontWeight: fontWeight.W500,
                          fontSize: fontSize.h4.large,
                          sx: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                        secondaryTypographyProps={{
                          fontFamily: FontFamily.Poppins,
                          fontSize: fontSize.caption.large,
                          sx: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                        primary={opt.name}
                        secondary={showPath ? opt.path : null}
                      />
                    </ListItemButton>
                    
                    {/* Render files in this folder if expanded */}
                    {isExpanded && (
                       fetchingFiles[folderId] ? (
                         <ListItemButton sx={{ pl: 2 + (opt.depth + 1) * 3, py: 0.5 }} disabled>
                           <CircularProgress size={16} sx={{ mr: 2 }} />
                           <Typography fontFamily={FontFamily.Poppins} fontSize={fontSize.caption.medium}>Loading files...</Typography>
                         </ListItemButton>
                       ) : (
                         (filesByFolder[folderId] || []).map((file) =>
                           renderFileRow(file, opt.depth + 1)
                         )
                       )
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
        
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2.5,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#344054",
              borderColor: "#d0d5dd",
              fontFamily: FontFamily.Poppins,
              fontWeight: fontWeight.W500,
              fontSize: fontSize.h4.large,
              "&:hover": { borderColor: "#d0d5dd", bgcolor: "#f9fafb" },
            }}
          >
            Cancel
          </Button>
          <CustomButton
            variant="contained"
            type="button"
            backgroundColor={ThemeColor.PURPLE}
            color={ThemeColor.WHITE}
            disabled={!selectedFile}
            onClick={handleSelect}
          >
            Select
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default FileManagerPickerModal;
